import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { API_ENDPOINTS } from "./apiConfig";
import { getStatusMessage } from "./statusMessages";
import { useAuth } from "./AuthContext";

function Login() {
  const { login, token } = useAuth();
  const navigate = useNavigate();

  // States
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [device, setDevice] = useState("web");
  const [errors, setErrors] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Redirect if token exists
  useEffect(() => {
    if (token && token.trim() !== "") {
      navigate("/search");
    }
  }, [token, navigate]);

  // Validation
  const validateEmail = (val) => {
    const trimmed = val.trim();
    if (!trimmed) return "Email is required";
    if (trimmed.length > 100) return "Email too long";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return "Invalid email";
    return "";
  };

  const validatePassword = (val) => {
    const trimmed = val.trim();
    if (trimmed.length < 8 || trimmed.length > 12)
      return "Password must be 8–12 characters";
    if (!/^[\x20-\x7E]*$/.test(trimmed))
      return "Invalid characters in password";
    return "";
  };

  const validateDevice = (val) => {
    const trimmed = val.trim();
    if (trimmed.length < 2 || trimmed.length > 12)
      return "Device name must be 2–12 characters";
    return "";
  };

  // Submit handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setErrors([]);
    setMessage("");
    setLoading(true);

    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    const deviceError = validateDevice(device);

    if (emailError || passwordError || deviceError) {
      setErrors([emailError, passwordError, deviceError].filter(Boolean));
      setLoading(false);
      return;
    }

    try {
      const res = await axios.post(API_ENDPOINTS.LOGIN, { email, password, device });
      const { result, token, user, pwd, errors: serverErrors } = res.data;

      if (result === 1) {
        login(token);
        setMessage("Login successful! 🎉");
        setTimeout(() => navigate("/search"), 300);
      } else {
        const allErrors = [];
        if (Array.isArray(serverErrors)) allErrors.push(...serverErrors);
        if (typeof pwd === "number" && pwd > 0) allErrors.push(getStatusMessage(pwd));
        setErrors(allErrors.length ? allErrors : ["Login failed."]);
      }
    } catch (err) {
      const { status, data } = err.response || {};
      if (status === 400 && Array.isArray(data?.errors)) {
        setErrors(data.errors);
      } else if (status === 400 && (data?.user || data?.pwd)) {
        const failMessages = [];
        if (typeof data.user === "number" && data.user!==1) failMessages.push(getStatusMessage(data.user));
        if (typeof data.pwd === "number") failMessages.push(getStatusMessage(data.pwd));
        setErrors(failMessages);
      } else if (data?.result === 0 && data?.user === 0) {
        setErrors(["You're not registered. Please sign up."]);
      } else {
        setErrors(["Server error. Please try again later."]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="d-flex flex-column min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffe0f0, #d0e8ff)",
        padding: "16px",
        paddingTop: "2rem",
      }}
    >
      {/* SEO Meta Tags */}
 {/* SEO Meta Tags */}
<Helmet>
  <title>Login | LuvMarry - Indian Dating, Matrimony & Marriage USA</title>
  
  <meta
    name="description"
    content="Login to LuvMarry - the most trusted Indian dating and matrimony website in the USA. Connect with verified Indian singles, NRI brides & grooms, and find your perfect life partner."
  />
  
  <meta
    name="keywords"
    content="LuvMarry login, Indian dating USA login, Indian matrimony USA login, NRI marriage login, Indian singles login, desi matrimony USA login, Indian matchmaking USA login"
  />
  
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href="https://usa.luvmarry.com/login" />

      {/* Open Graph for social sharing */}
      <meta property="og:title" content="Login | LuvMarry - Indian Matrimony & Dating USA" />
      <meta 
        property="og:description" 
        content="Sign in to LuvMarry and meet Indian singles & NRI brides & grooms. A secure matrimony platform for love, dating & marriage in the USA." 
      />
      <meta property="og:image" content="/LuvMarryLogoPink.png" />
      <meta property="og:url" content="https://usa.luvmarry.com/login" />
      <meta property="og:type" content="website" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Login | LuvMarry - Indian Dating & Marriage USA" />
      <meta 
        name="twitter:description" 
        content="Log in to connect with verified Indian singles and start your marriage journey on LuvMarry." 
      />
      <meta name="twitter:image" content="/LuvMarryLogoPink.png" />
    </Helmet>


      <main style={{ maxWidth: 480, width: "100%", margin: "0 auto" }}>
        <h1 className="text-center wedding-heading">
          <img
            src="/LuvMarryLogoPink.png"
            alt="LuvMarry Logo"
            width="48"
            height="48"
            style={{ marginRight: "8px" }}
          />
          Login
        </h1>

        <form onSubmit={handleLogin} noValidate>
          <div className="mb-3">
            <label htmlFor="email" className="form-label text-muted">
              Email address
            </label>
            <input
              id="email"
              type="email"
              className="form-control"
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="mb-3">
            <label htmlFor="password" className="form-label text-muted">
              Password
            </label>
            <input
              id="password"
              type="password"
              className="form-control"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          <button
            type="submit"
            className="btn w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
            style={{
              backgroundColor: "#ff69b4",
              color: "white",
              fontWeight: "600",
              borderRadius: "30px",
              border: "none",
              padding: "0.6rem 1.2rem",
              fontSize: "1rem",
            }}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="bi bi-arrow-repeat me-2 spin" /> Logging in...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-right me-2" /> Login
              </>
            )}
          </button>
        </form>

        {message && (
          <div
            className="alert alert-success mt-3"
            role="alert"
            style={{ whiteSpace: "pre-line" }}
          >
            {message}
          </div>
        )}

        {errors.length > 0 && (
          <div
            className="alert alert-danger mt-3"
            role="alert"
            style={{ backgroundColor: "#ffe0e0", color: "#cc0000" }}
          >
            <ul className="mb-0">
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-center mt-3">
          <a href="/ForgotPassword" className="text-primary">
            Forgot password?
          </a>
        </div>

        <div className="text-center mt-4">
          <small className="text-muted">
            Don’t have an account?{" "}
            <a href="/signup" className="text-primary">
              Sign up
            </a>
          </small>
        </div>
      </main>
    </div>
  );
}

export default Login;
